import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';

import { Resource } from '../../../../../common/constants/resource';
import { PricingSelect } from './components';
import { MemoizedLetter } from './components/MemoizedLetter';
import PdfViewer from './components/PdfViewer';
import { getAvailableSources } from './config';
import {
  LETTER_DEFAULT_EMAIL,
  LETTER_DEFAULT_PHONE,
  sourceHiddenLetterTypes,
  sourceOptions,
} from './constants';
import { LetterForm } from './containers';
import { useGenerate } from './hooks';
import { LetterFormData } from './hooks/useLetterForm';
import { Data, LetterTypes, Sources } from './types';

import {
  FlexCol,
  FlexRow,
  MultiSelect,
  NoraButton,
  Select,
} from '../../../../../common/components/v2';
import { MultiSelectOption } from '../../../../../common/components/v2/form/MultiSelect';
import { Carriers } from '../../../../../common/constants/carriers';
import { getRiderLabel } from '../../../../../common/constants/rider';
import styles from './styles.module.scss';
import { getLetterTypeLabel } from './utils';

export interface LetterGeneratorProps {
  initialLetterType: LetterTypes;
  isOpen: boolean;
  onClose: () => void;
  phone?: string;
  email?: string;
  date?: string;
  carrier: Carriers;
  fullName: string;
  onSuccess: (files: File[]) => void;
  policyNumber: string;
  riders: string[];
  isJuvenile?: boolean;
  proposedInsuredName?: string;
}

const LetterGenerator = ({
  initialLetterType,
  onClose,
  isOpen,
  phone = LETTER_DEFAULT_PHONE,
  email = LETTER_DEFAULT_EMAIL,
  date = dayjs().format('YYYY-MM-DD'),
  carrier,
  fullName,
  onSuccess,
  policyNumber,
  riders,
  isJuvenile = false,
  proposedInsuredName,
}: LetterGeneratorProps) => {
  const [data, setData] = useState<Data | null>(null);
  const [source, setSource] = useState<Sources | null>(null);
  const [isFCRAIncluded, setFCRAIncluded] = useState(false);
  const [selectedRiders, setSelectedRiders] = useState<string[]>([]);
  const { generate } = useGenerate({ isFCRAIncluded });
  const [letterType, setLetterType] = useState<LetterTypes>(initialLetterType);
  const [isRidersDirty, setIsRidersDirty] = useState(false);

  const isSourceVisible = useMemo(
    () => !sourceHiddenLetterTypes.includes(letterType),
    [letterType],
  );

  const ridersOptions = useMemo<MultiSelectOption<string>[]>(
    () => riders.map(value => ({ label: getRiderLabel(value), value })),
    [riders],
  );

  const filteredSourcesOptions = useMemo(() => {
    const availableSources = getAvailableSources(letterType);
    return sourceOptions.filter(({ value }) =>
      availableSources.includes(value),
    );
  }, [letterType]);

  const sourceValue = useMemo(
    () => filteredSourcesOptions.find(({ value }) => value === source),
    [source, filteredSourcesOptions],
  );

  const isRidersInvalid = useMemo(
    () => letterType === LetterTypes.otafSamePrice && !selectedRiders.length,
    [letterType, selectedRiders.length],
  );

  const ridersError = useMemo(() => {
    if (!isRidersInvalid || !isRidersDirty) return;
    return 'Riders are required';
  }, [isRidersInvalid, isRidersDirty]);

  const handleOnHiddenSourcePreview = useCallback(() => {
    setData({
      source: null,
      letterType,
      letter: {},
      phone,
      email,
      date,
      carrier,
      fullName,
      policyNumber,
      riders: [],
      isJuvenile,
      insuredName: proposedInsuredName ?? '',
    });
  }, [
    letterType,
    phone,
    email,
    date,
    carrier,
    fullName,
    policyNumber,
    isJuvenile,
    proposedInsuredName,
  ]);

  const handleOnSuccess = useCallback(
    async (letter: LetterFormData) => {
      if (!source || isRidersInvalid) return;
      setData({
        source,
        letterType,
        letter,
        phone,
        email,
        date,
        carrier,
        fullName,
        policyNumber,
        riders: selectedRiders,
        isJuvenile,
        insuredName: proposedInsuredName ?? '',
      });
    },
    [
      source,
      letterType,
      phone,
      email,
      date,
      carrier,
      fullName,
      policyNumber,
      selectedRiders,
      isRidersInvalid,
      isJuvenile,
      proposedInsuredName,
    ],
  );

  const handleOnClose = useCallback(() => {
    setLetterType(initialLetterType);
    setSource(null);
    setSelectedRiders([]);
    setData(null);
    setFCRAIncluded(false);
    onClose();
  }, [onClose, initialLetterType]);

  const handleOnUpload = useCallback(async () => {
    if (!data) return;
    const files = await generate(data);
    onSuccess(files);
    handleOnClose();
  }, [data, generate, onSuccess, handleOnClose]);

  const handleOnPricingChange = useCallback((value: LetterTypes) => {
    setLetterType(value);
    setSource(null);
    setData(null);
    setSelectedRiders([]);
    setIsRidersDirty(false);
  }, []);

  const onRidersChange = useCallback((value: string[]) => {
    setIsRidersDirty(true);
    setSelectedRiders(value);
  }, []);

  return (
    <Dialog fullWidth maxWidth="lg" open={isOpen} onClose={handleOnClose}>
      <DialogTitle>
        {getLetterTypeLabel(letterType)} - Letter generator
      </DialogTitle>
      <DialogContent>
        <FlexRow>
          <FlexCol className={styles.LeftSide} xs={4}>
            {initialLetterType === LetterTypes.otaf && (
              <PricingSelect
                letterType={letterType}
                onChange={handleOnPricingChange}
              />
            )}
            {isSourceVisible && (
              <Select
                fullWidth
                title="Source"
                placeholder={Resource.SELECT_PLACEHOLDER}
                value={sourceValue}
                onChange={({ value }) => setSource(value as Sources)}
                options={filteredSourcesOptions}
              />
            )}
            {initialLetterType !== LetterTypes.withdrawal && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFCRAIncluded}
                    onChange={e => setFCRAIncluded(e.target.checked)}
                    color="primary"
                  />
                }
                label="FCRA attachment"
              />
            )}
            {source && (
              <LetterForm
                isDisabled={isRidersInvalid}
                key={`${letterType}-${source}`}
                letterType={letterType}
                source={source}
                onSuccess={handleOnSuccess}
              >
                {initialLetterType === LetterTypes.otaf && (
                  <MultiSelect
                    fullWidth
                    label="Riders"
                    required={letterType === LetterTypes.otafSamePrice}
                    variant="standard"
                    placeholder={Resource.SELECT_PLACEHOLDER}
                    value={selectedRiders}
                    error={ridersError}
                    onChange={onRidersChange}
                    options={ridersOptions}
                  />
                )}
              </LetterForm>
            )}
            {!isSourceVisible && (
              <NoraButton onClick={handleOnHiddenSourcePreview}>
                Preview
              </NoraButton>
            )}
          </FlexCol>
          <FlexCol xs={8}>
            {data && (
              <PdfViewer>
                <MemoizedLetter data={data} />
              </PdfViewer>
            )}
          </FlexCol>
        </FlexRow>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose}>Close</Button>
        {data && <Button onClick={handleOnUpload}>Upload</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default LetterGenerator;
